import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { searchLocators } from '../../services/locatorManagementService';
import { updateLocatorAsync } from './updateLocatorSlice';

export const searchLocatorsAsync = createAsyncThunk(
  'locatorManagement/search',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await searchLocators(params);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || 'Search failed. Check API connection.',
      );
    }
  },
);

const searchLocatorsSlice = createSlice({
  name: 'searchLocators',
  initialState: { rows: [], loading: false, error: null, successMsg: null, total: null },
  reducers: {
    clearMessages(state) {
      state.error = null;
      state.successMsg = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchLocatorsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMsg = null;
      })
      .addCase(searchLocatorsAsync.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        // Normalize payload into an array of rows and an optional total count
        let rows = [];
        let total = null;

        if (Array.isArray(payload)) {
          rows = payload;
          total = payload.length;
        } else if (payload && Array.isArray(payload.results)) {
          rows = payload.results;
          total = payload.count || payload.total || (payload.meta && payload.meta.total) || null;
        } else if (payload && Array.isArray(payload.data)) {
          rows = payload.data;
          total = payload.count || payload.total || (payload.meta && payload.meta.total) || null;
        } else if (payload && typeof payload === 'object') {
          // Try to find any array-valued key in the payload (defensive)
          const keys = Object.keys(payload);
          for (const k of keys) {
            if (Array.isArray(payload[k])) {
              rows = payload[k];
              break;
            }
          }
          total = payload.count || payload.total || (payload.meta && payload.meta.total) || total;
        }

        state.rows = rows;
        state.total = total;
        if (rows.length === 0) state.error = 'No locators found.';
      })
      .addCase(searchLocatorsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateLocatorAsync.fulfilled, (state, action) => {
        const { id, locator_type, locator_value } = action.meta.arg;
        const row = state.rows.find((r) => r.id === id);
        if (row) {
          row.locator_type = locator_type;
          row.locator_value = locator_value;
        }
      });
  },
});

export const { clearMessages } = searchLocatorsSlice.actions;
export const selectSearchRows = (state) => state.searchLocators.rows;
export const selectSearchLoading = (state) => state.searchLocators.loading;
export const selectSearchError = (state) => state.searchLocators.error;
export const selectSearchSuccessMsg = (state) => state.searchLocators.successMsg;
export const selectSearchTotal = (state) => state.searchLocators.total;
export default searchLocatorsSlice.reducer;
